import React, { useState, useEffect } from 'react';
import { 
  User, 
  Mail, 
  Phone, 
  MapPin, 
  Calendar, 
  Clock, 
  Users, 
  Award,
  CheckCircle,
  AlertCircle,
  Loader,
  X
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../config/api';

const VolunteerApplicationForm = ({ opportunity, onClose, onSuccess, logoUrl }) => {
  const { currentUser } = useAuth();
  
  const [formData, setFormData] = useState({
    applicantName: currentUser ? (currentUser.displayName || currentUser.email || '') : '',
    applicantEmail: currentUser ? (currentUser.email || '') : '',
    applicantPhone: currentUser ? (currentUser.phone || '') : '',
    applicantAge: '',
    timeCommitment: '',
    backgroundCheckConsent: false
  });

  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const timeCommitmentOptions = [
    '1-2 hours per week',
    '3-5 hours per week',
    '5-10 hours per week',
    '10+ hours per week',
    'Event-based only',
    'Flexible'
  ];

  useEffect(() => {
    if (currentUser) {
      setFormData(prev => ({
        ...prev,
        applicantName: currentUser.displayName || currentUser.email || '',
        applicantEmail: currentUser.email || '',
        applicantPhone: currentUser.phone || ''
      }));
    }
  }, [currentUser]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    if (error) setError('');
  };

  const validateStep = (step) => {
    setError('');
    
    if (step === 1) {
      if (!formData.applicantName || !formData.applicantEmail || !formData.applicantAge) {
        setError('Please fill in all required fields');
        return false;
      }
      if (!formData.timeCommitment) {
        setError('Please select a time commitment');
        return false;
      }
    }
    
    if (step === 2) {
      if (!formData.backgroundCheckConsent) {
        setError('You must consent to a background check to volunteer');
        return false;
      }
    }
    
    return true;
  };

  const nextStep = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => Math.min(prev + 1, 2));
    }
  };

  const prevStep = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateStep(2)) {
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      const response = await api.post('/volunteer/applications', {
        opportunityId: opportunity.id,
        opportunityTitle: opportunity.title,
        ...formData
      });

      if (response.ok) {
        setSuccess(true);
        if (onSuccess) onSuccess();
        setTimeout(() => {
          onClose();
        }, 2000);
      } else {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to submit application');
      }
    } catch (err) {
      console.error('Error submitting volunteer application:', err);
      setError(err.message || 'Failed to submit application. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (success) {
    return (
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[9999] p-3 sm:p-4 animate-fadeIn" onClick={(e) => e.target === e.currentTarget && onClose()}>
        <div className="bg-white rounded-2xl sm:rounded-3xl max-w-sm sm:max-w-md w-full p-5 sm:p-6 text-center shadow-2xl border border-gray-100 my-auto animate-scaleUp">
          <div className="w-14 h-14 sm:w-16 sm:h-16 bg-gradient-to-br from-secondary-500 to-secondary-600 rounded-full flex items-center justify-center mx-auto mb-3 sm:mb-4 shadow-lg overflow-hidden">
            {logoUrl ? (
              <img 
                src={logoUrl} 
                alt="BOW Logo" 
                className="w-full h-full object-contain"
              />
            ) : (
              <CheckCircle className="w-8 h-8 text-white" />
            )}
          </div>
          <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-1.5">Application Submitted!</h3>
          <p className="text-xs sm:text-sm text-gray-600 mb-4 px-2 leading-relaxed">
            Thank you for your interest in volunteering with BOW! Our volunteer coordinator will reach out soon.
          </p>
          <button
            onClick={onClose}
            className="px-5 py-2 sm:py-2.5 rounded-xl bg-primary-600 hover:bg-primary-700 text-white font-bold text-xs sm:text-sm shadow-md transition-all duration-200 hover:scale-105 w-full"
          >
            Close
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-2.5 sm:p-4 md:p-6 bg-black/60 backdrop-blur-sm overflow-y-auto animate-fadeIn" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="relative w-full max-w-lg sm:max-w-2xl max-h-[85vh] sm:max-h-[90vh] flex flex-col bg-white rounded-2xl sm:rounded-3xl shadow-2xl border border-gray-100 overflow-hidden my-auto animate-scaleUp transform transition-all" role="dialog" aria-modal="true">
        {/* Header */}
        <div className="p-3.5 sm:p-6 border-b border-gray-100 flex items-center justify-between shrink-0 bg-white">
          <div className="flex items-center flex-1 min-w-0 pr-3">
            {logoUrl && (
              <img 
                src={logoUrl} 
                alt="BOW Logo" 
                className="w-8 h-8 sm:w-10 sm:h-10 rounded-full mr-2.5 sm:mr-3 object-contain shrink-0"
              />
            )}
            <div className="min-w-0">
              <h2 className="text-base sm:text-xl md:text-2xl font-bold text-gray-900 tracking-tight truncate">Volunteer Application</h2>
              <p className="text-[11px] sm:text-sm text-gray-500 mt-0.5 truncate">
                Applying for: <span className="font-semibold text-primary-600">{opportunity.title}</span>
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="shrink-0 p-1.5 sm:p-2 rounded-full bg-gray-100 hover:bg-gray-200 text-gray-500 hover:text-gray-700 transition-colors focus:outline-none"
            aria-label="Close modal"
            type="button"
          >
            <X className="w-4 h-4 sm:w-5 sm:h-5" />
          </button>
        </div>

        {/* Form Body */}
        <form onSubmit={handleSubmit} className="p-3.5 sm:p-6 space-y-2.5 sm:space-y-4 flex-1 overflow-y-auto min-h-0">
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-2.5 sm:p-3 flex items-start">
              <AlertCircle className="w-4 h-4 text-red-500 mr-2 mt-0.5 flex-shrink-0" />
              <div className="min-w-0">
                <h4 className="text-red-800 font-semibold text-xs sm:text-sm">Error</h4>
                <p className="text-red-700 text-[11px] sm:text-xs mt-0.5 break-words">{error}</p>
              </div>
            </div>
          )}

          {/* Progress Indicator */}
          <div className="flex items-center justify-center mb-1 sm:mb-2">
            <div className="flex items-center space-x-3 sm:space-x-4">
              <div className={`w-6 h-6 sm:w-7 sm:h-7 rounded-full flex items-center justify-center text-xs font-bold ${
                currentStep >= 1 ? 'bg-primary-600 text-white' : 'bg-gray-200 text-gray-600'
              }`}>
                1
              </div>
              <div className={`w-10 sm:w-14 h-1 rounded-full ${
                currentStep >= 2 ? 'bg-primary-600' : 'bg-gray-200'
              }`} />
              <div className={`w-6 h-6 sm:w-7 sm:h-7 rounded-full flex items-center justify-center text-xs font-bold ${
                currentStep >= 2 ? 'bg-primary-600 text-white' : 'bg-gray-200 text-gray-600'
              }`}>
                2
              </div>
            </div>
          </div>

          {/* Step 1: Personal Info & Availability */}
          {currentStep === 1 && (
            <div className="space-y-2.5 sm:space-y-3.5">
              <div>
                <h3 className="text-xs sm:text-sm font-bold text-gray-900 mb-2">
                  Personal Information
                </h3>
                
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 sm:gap-3">
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1">
                      Full Name *
                    </label>
                    <input
                      type="text"
                      value={formData.applicantName}
                      onChange={(e) => handleInputChange('applicantName', e.target.value)}
                      className={`w-full px-3 py-1.5 sm:py-2 text-xs sm:text-sm rounded-xl border border-gray-200 focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all shadow-sm ${
                        currentUser ? 'bg-gray-50 text-gray-500' : 'bg-white'
                      }`}
                      required
                      readOnly={!!currentUser}
                      disabled={!!currentUser}
                    />
                    {currentUser && (
                      <p className="text-[10px] text-gray-400 mt-0.5">Pre-filled from account</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1">
                      Email *
                    </label>
                    <input
                      type="email"
                      value={formData.applicantEmail}
                      onChange={(e) => handleInputChange('applicantEmail', e.target.value)}
                      className={`w-full px-3 py-1.5 sm:py-2 text-xs sm:text-sm rounded-xl border border-gray-200 focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all shadow-sm ${
                        currentUser ? 'bg-gray-50 text-gray-500' : 'bg-white'
                      }`}
                      required
                      readOnly={!!currentUser}
                      disabled={!!currentUser}
                    />
                    {currentUser && (
                      <p className="text-[10px] text-gray-400 mt-0.5">Pre-filled from account</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1">
                      Phone *
                    </label>
                    <input
                      type="tel"
                      value={formData.applicantPhone}
                      onChange={(e) => handleInputChange('applicantPhone', e.target.value)}
                      className="w-full px-3 py-1.5 sm:py-2 text-xs sm:text-sm bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all shadow-sm"
                      required
                      placeholder="Phone number"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1">
                      Age *
                    </label>
                    <input
                      type="number"
                      min="16"
                      value={formData.applicantAge}
                      onChange={(e) => handleInputChange('applicantAge', e.target.value)}
                      className="w-full px-3 py-1.5 sm:py-2 text-xs sm:text-sm bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all shadow-sm"
                      required
                    />
                  </div>
                </div>

                <div className="mt-2.5 sm:mt-3">
                  <label className="block text-xs font-semibold text-gray-700 mb-1">
                    Time Commitment *
                  </label>
                  <select
                    value={formData.timeCommitment}
                    onChange={(e) => handleInputChange('timeCommitment', e.target.value)}
                    className="w-full px-3 py-1.5 sm:py-2 text-xs sm:text-sm bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all shadow-sm"
                    required
                  >
                    <option value="">Select time commitment</option>
                    {timeCommitmentOptions.map(option => (
                      <option key={option} value={option}>{option}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          )}

          {/* Step 2: Consent */}
          {currentStep === 2 && (
            <div className="space-y-2.5 sm:space-y-3">
              <div>
                <h3 className="text-xs sm:text-sm font-bold text-gray-900 mb-2">
                  Consent & Agreement
                </h3>
                
                <div className="space-y-2.5">
                  <div className="bg-gray-50 p-3 rounded-xl border border-gray-200/70">
                    <h4 className="font-semibold text-xs text-gray-900 mb-1">Volunteer Agreement</h4>
                    <ul className="text-[11px] sm:text-xs text-gray-600 space-y-1">
                      <li>• I understand this is a volunteer position with no monetary compensation</li>
                      <li>• I agree to represent BOW professionally and follow all policies</li>
                      <li>• I will provide advance notice if I cannot fulfill my commitments</li>
                      <li>• I understand that BOW reserves the right to terminate my volunteer status</li>
                      <li>• I give permission for BOW to use photos/videos of me for promotional purposes</li>
                    </ul>
                  </div>

                  <div className="flex items-start">
                    <input
                      type="checkbox"
                      checked={formData.backgroundCheckConsent}
                      onChange={(e) => handleInputChange('backgroundCheckConsent', e.target.checked)}
                      className="mt-0.5 mr-2 w-4 h-4 flex-shrink-0 text-primary-600 rounded border-gray-300 focus:ring-primary-500"
                      required
                    />
                    <div className="min-w-0">
                      <label className="text-xs font-semibold text-gray-700 block">
                        Background Check Consent *
                      </label>
                      <p className="text-[11px] text-gray-500 mt-0.5 break-words">
                        I consent to a background check as required for this volunteer position. 
                        This is for the safety and security of our community members.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Navigation Buttons */}
          <div className="pt-2.5 sm:pt-3 flex items-center justify-end gap-2.5 border-t border-gray-100 shrink-0">
            {currentStep > 1 && (
              <button
                type="button"
                onClick={prevStep}
                className="px-3.5 py-1.5 sm:py-2 text-xs sm:text-sm font-semibold text-gray-600 hover:text-gray-800 transition-colors"
              >
                Previous
              </button>
            )}
            <button
              type="button"
              onClick={onClose}
              className="px-3.5 py-1.5 sm:py-2 text-xs sm:text-sm font-semibold text-gray-600 hover:text-gray-800 transition-colors"
            >
              Cancel
            </button>
            
            {currentStep < 2 ? (
              <button
                type="button"
                onClick={nextStep}
                className="inline-flex items-center justify-center px-5 py-2 sm:py-2.5 rounded-xl bg-primary-600 hover:bg-primary-700 text-white font-bold text-xs sm:text-sm shadow-md transition-all duration-200 hover:scale-105"
              >
                Next
              </button>
            ) : (
              <button
                type="submit"
                disabled={isSubmitting}
                className="inline-flex items-center justify-center gap-1.5 px-5 py-2 sm:py-2.5 rounded-xl bg-primary-600 hover:bg-primary-700 text-white font-bold text-xs sm:text-sm shadow-md transition-all duration-200 hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
              >
                {isSubmitting ? (
                  <>
                    <Loader className="w-3.5 h-3.5 animate-spin" />
                    <span>Submitting...</span>
                  </>
                ) : (
                  'Submit Application'
                )}
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};

export default VolunteerApplicationForm;